import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { ref, onBeforeUnmount, toRefs } from 'vue';
import { QInput, useQuasar } from 'quasar';
import { useFocus } from '@vueuse/core';

// Personal Components
import QBtnCancel from '@components/form/QBtnCancel.vue';
import QBtnValid from '@components/form/QBtnValid.vue';
import QDialogTemplate from '@components/template/QDialogTemplate.vue';

// Personal libs
import { checkEmail } from '@shared/lib/security.lib';
import {
  displayNotifSuccess,
  displayDialogError,
} from '@shared/lib/notify.lib';
import { $t } from '@i18n/utils';
import authRepository from '@data/repository/auth.repository';
import { emailRules, emailExample } from '@shared/lib/form.lib';
import { DialogSizeEnum } from '@shared/enum/dialog-size.enum';

/**
 * Globals
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'DialogForgottenPass',
  props: {
  show: { required: false, type: Boolean, default: false },
},
  emits: ['close'],
  setup(__props, { emit: __emit }) {

// Vendor
const $q = useQuasar();

/**
 * Events
 */
const emits = __emit;

/**
 * HTMLElement ref
 */
const qInputEmailRef = ref<QInput>();
const { focused: qInputEmailRefFocused } = useFocus(qInputEmailRef);

/**
 * Datas
 */
const email = ref<string | null>(null);
const loading = ref<boolean | undefined>(false);

/**
 * Properties
 */
const props = __props;
const { show } = toRefs(props);

/**
 * Methods
 */
function onClose() {
  emits('close');
}

function onBtnClose() {
  onClose();
}

// email
function clearEmail() {
  qInputEmailRef.value?.resetValidation();
  email.value = null;
  qInputEmailRefFocused.value = true;
}

async function onSubmit() {
  qInputEmailRef.value?.validate();

  if (qInputEmailRef.value?.hasError || email.value == null) {
    return;
  }

  loading.value = true;

  authRepository
    .forgottenPass(email.value)
    .then(() => {
      loading.value = false;

      displayNotifSuccess($t('forgottenPass.msg.success'));

      // Close popup
      onClose();
    })
    .catch((error: Error) => {
      displayDialogError({
        title: 'Erreur',
        message: error.message ?? 'null',
        isHtml: true
      });
    })
    .finally(() => {
      loading.value = false;
      clearEmail();
    });
}

/**
 * ****************************** Hooks
 */
onBeforeUnmount(() => {
  email.value = null;
  loading.value = undefined;
});

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!

  return (_openBlock(), _createBlock(QDialogTemplate, {
    ref: "qDialogTemplate",
    "show-dialog": _unref(show),
    title: _unref($t)('forgottenPass.title'),
    maximized: false,
    "dialog-size": _unref(DialogSizeEnum).SMALL,
    onClose: _cache[3] || (_cache[3] = ($event: any) => (onClose()))
  }, {
    content: _withCtx(() => [
      _createVNode(_component_q_card_section, { class: "" }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref($t)('forgottenPass.desc')), 1 /* TEXT */)
        ]),
        _: 1 /* STABLE */
      }),
      _createVNode(_component_q_card_section, {
        id: "email_example",
        class: "q-pt-none"
      }, {
        default: _withCtx(() => [
          _createTextVNode(" Exemple : " + _toDisplayString(_unref(emailExample)), 1 /* TEXT */)
        ]),
        _: 1 /* STABLE */
      }),
      _createVNode(_component_q_card_section, { class: "q-pt-none" }, {
        default: _withCtx(() => [
          _createVNode(_unref(QInput), {
            ref_key: "qInputEmailRef",
            ref: qInputEmailRef,
            "aria-required": "true",
            "aria-describedby": "email_example",
            "aria-invalid": _unref(checkEmail)(email.value ?? undefined) ? 'false' : 'true',
            outlined: "",
            modelValue: email.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((email).value = $event)),
            disable: loading.value,
            type: "email",
            autocomplete: "email",
            label: _unref($t)('form.email.label'),
            "bg-color": _unref($q).dark.isActive ? 'black' : 'white',
            "label-color": _unref($q).dark.isActive ? 'white' : 'black',
            "input-class": [
            _unref($q).dark.isActive ? 'text-white' : 'text-black',
            'set-outline',
          ],
            "hide-bottom-space": "",
            "lazy-rules": "",
            rules: _unref(emailRules),
            enterkeyhint: "done"
          }, {
            prepend: _withCtx(() => [
              _createVNode(_component_q_icon, { name: "mail" })
            ]),
            append: _withCtx(() => [
              _createVNode(_component_q_btn, {
                flat: "",
                round: "",
                dense: "",
                icon: "cancel",
                class: "set-outline",
                title: _unref($t)('form.email.reset'),
                onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (clearEmail()), ["stop"]))
              }, null, 8 /* PROPS */, ["title"])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["aria-invalid", "modelValue", "disable", "label", "bg-color", "label-color", "input-class", "rules"])
        ]),
        _: 1 /* STABLE */
      })
    ]),
    footer: _withCtx(() => [
      _createVNode(_component_q_card_actions, { align: "right" }, {
        default: _withCtx(() => [
          _createVNode(QBtnCancel, {
            label: _unref($t)('forgottenPass.btn.cancel.label'),
            disable: loading.value,
            onClick: _cache[2] || (_cache[2] = ($event: any) => (onBtnClose()))
          }, null, 8 /* PROPS */, ["label", "disable"]),
          _createVNode(QBtnValid, {
            label: _unref($t)('forgottenPass.btn.ok.label'),
            "loading-label": _unref($t)('forgottenPass.btn.ok.loadingLabel'),
            disable: loading.value,
            loading: loading.value,
            "icon-right": 'send',
            onClick: onSubmit
          }, null, 8 /* PROPS */, ["label", "loading-label", "disable", "loading"])
        ]),
        _: 1 /* STABLE */
      })
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["show-dialog", "title", "dialog-size"]))
}
}

})